import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import theme from '../styles/theme';
import SpineModelViewer from '../components/SpineModelViewer';
import SearchableDropdown from '../components/SearchableDropdown';
import { useLocalStorage } from '../utils/hooks';
import { api } from '../services/api';
import '@kitware/vtk.js/Rendering/Profiles/Geometry';
import vtkFullScreenRenderWindow from '@kitware/vtk.js/Rendering/Misc/FullScreenRenderWindow';
import vtkSTLReader from '@kitware/vtk.js/IO/Geometry/STLReader';
import vtkActor from '@kitware/vtk.js/Rendering/Core/Actor';
import vtkMapper from '@kitware/vtk.js/Rendering/Core/Mapper';
import vtkTriangleFilter from '@kitware/vtk.js/Filters/General/TriangleFilter';
import vtkInteractorStyleTrackballCamera from '@kitware/vtk.js/Interaction/Style/InteractorStyleTrackballCamera';
import vtkLight from '@kitware/vtk.js/Rendering/Core/Light';
import Papa from 'papaparse';
import API_URL from '../config/api';

// Define vertebral levels
const vertebralLevels = [
  'C1', 'C2', 'C3', 'C4', 'C5', 'C6', 'C7',
  'T1', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'T8', 'T9', 'T10', 'T11', 'T12',
  'L1', 'L2', 'L3', 'L4', 'L5', 'S1'
];

// Helper function to create default spine data
const createDefaultSpineData = (level) => ({
  level,
  pedicleWidthR: 'N/A',
  pedicleWidthL: 'N/A',
  isLoading: false,
  hasError: false
});

function PlanningPage() {
  const navigate = useNavigate();
  const [originalSpineImage, setOriginalSpineImage] = useState(null);
  const [translatedSpineImage, setTranslatedSpineImage] = useState(null);
  const [imagesLoading, setImagesLoading] = useState(false);
  const [imageError, setImageError] = useState(null);
  const [selectedRegions, setSelectedRegions] = useState({
    cervical: true,
    thoracic: true,
    lumbar: true,
    sacral: true
  });
  const [viewMode, setViewMode] = useState('CT');
  const [currentStage, setCurrentStage] = useState(1); // 1 = Intervention Planning, 2 = Pre-op Planning
  const [hasInterventions, setHasInterventions] = useState(false);

  // Measurement data states
  const [spineData, setSpineData] = useState(vertebralLevels.map(level => createDefaultSpineData(level)));
  const [measurementsLoading, setMeasurementsLoading] = useState(false);
  const [measurementsError, setMeasurementsError] = useState(null);
  const [loadedLevels, setLoadedLevels] = useState(new Set());

  // Load selected levels from anatomical measurements
  const [anatomicalSelectedRows] = useLocalStorage('anatomical-selected-rows', []);
  const [concerningRows] = useLocalStorage('anatomical-concerning-rows', []);
  const [herniatedRows] = useLocalStorage('anatomical-herniated-rows', []);

  // Load intervention vertebrae from Parameters screen
  const [vertebralInterventions, setVertebralInterventions] = useLocalStorage('vertebral-interventions', {});
  const [intervertebralInterventions, setIntervertebralInterventions] = useLocalStorage('intervertebral-interventions', {});

  // const [correctionDeltas, setCorrectionDeltas] = useState({}); // REMOVED
  const [spineDistractions, setSpineDistractions] = useState({}); // New state for height distractions
  // Hardware selections
  const [interbodyDeviceSelections, setInterbodyDeviceSelections] = useLocalStorage('interbody-device-selections', {});
  const [cageSelections, setCageSelections] = useLocalStorage('cage-selections', {});
  const [pedicleLandmarks, setPedicleLandmarks] = useState({});

  // State for spine corrections (lordosis/kyphosis)
  const [spineCorrections, setSpineCorrections] = useState({});
  const [initialAngles, setInitialAngles] = useState({}); // Store initial X-ray angles

  // Pedicle screw selections state
  const [pedicleScrewSelections, setPedicleScrewSelections] = useLocalStorage('pedicle-screw-selections', {});
  const [plateScrewLengths, setPlateScrewLengths] = useLocalStorage('plate-screw-lengths', {});

  // Instrumentation data states
  const [pedicleScrewsData, setPedicleScrewsData] = useState([]);
  const [instrumentationLoading, setInstrumentationLoading] = useState(false);
  const [matchedScrews, setMatchedScrews] = useState({});

  // Get vertebrae that have interventions selected
  const interventionVertebrae = [
    ...Object.keys(vertebralInterventions).filter(level => vertebralInterventions[level]),
    ...Object.keys(intervertebralInterventions).flatMap(level => {
      if (intervertebralInterventions[level]) {
        return level.split('-'); // Split "L3-L4" into ["L3", "L4"]
      }
      return [];
    })
  ].filter((level, index, array) => array.indexOf(level) === index); // Remove duplicates

  // Helper function to format measurements to 1 decimal place
  const formatMeasurement = (value) => {
    if (!value || value === 'N/A') return 'N/A';
    const numValue = parseFloat(value);
    return isNaN(numValue) ? 'N/A' : numValue.toFixed(1);
  };

  // Function to handle vertebral intervention selection
  const handleVertebralInterventionChange = (level, intervention) => {
    setVertebralInterventions(prev => ({
      ...prev,
      [level]: intervention
    }));
    checkForInterventions();
  };

  // Function to handle intervertebral intervention selection
  const handleIntervertebralInterventionChange = (level, intervention) => {
    setIntervertebralInterventions(prev => ({
      ...prev,
      [level]: intervention
    }));
    checkForInterventions();
  };

  // Check if any interventions are selected
  const checkForInterventions = () => {
    const hasVertebral = Object.values(vertebralInterventions).some(value => value);
    const hasIntervertebral = Object.values(intervertebralInterventions).some(value => value);
    setHasInterventions(hasVertebral || hasIntervertebral || Object.values(pedicleScrewSelections).some(v => v));
  };

  // Check for interventions on mount and when interventions change
  useEffect(() => {
    checkForInterventions();
  }, [vertebralInterventions, intervertebralInterventions, pedicleScrewSelections]);

  // Get vertebral levels from anatomical selected rows
  const selectedVertebralLevels = anatomicalSelectedRows.filter(level =>
    !level.includes('-')
  );

  // Get intervertebral levels function
  const getIntervertebralLevels = () => {
    if (anatomicalSelectedRows.length <= 1) return [];

    const spineOrder = ['C1', 'C2', 'C3', 'C4', 'C5', 'C6', 'C7',
      'T1', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'T8', 'T9', 'T10', 'T11', 'T12',
      'L1', 'L2', 'L3', 'L4', 'L5', 'S1'];

    const sortedLevels = [...anatomicalSelectedRows].sort((a, b) => {
      return spineOrder.indexOf(a) - spineOrder.indexOf(b);
    });

    const intervertebralLevels = [];
    for (let i = 0; i < sortedLevels.length - 1; i++) {
      const currentIndex = spineOrder.indexOf(sortedLevels[i]);
      const nextIndex = spineOrder.indexOf(sortedLevels[i + 1]);

      if (nextIndex === currentIndex + 1) {
        intervertebralLevels.push(`${sortedLevels[i]}-${sortedLevels[i + 1]}`);
      }
    }

    return intervertebralLevels;
  };

  const intervertebralLevels = getIntervertebralLevels();

  // Helper to determine if a value should be highlighted (red)
  // Helper to determine if a value should be highlighted (red) - only pedicle width < 4mm
  const isHighlighted = (level, column, value) => {
    // Parse numeric value if it contains numbers
    const numericValue = parseFloat(value);
    if (isNaN(numericValue)) return false;

    // Only highlight pedicle width measurements that are less than 4mm
    if (column === 'pedicleWidthR' || column === 'pedicleWidthL') {
      return numericValue < 4.0;
    }

    return false;
  };

  // Function to handle pedicle screw selection
  const handlePedicleScrewChange = (level, side) => {
    const key = `${level}_${side}`;
    console.log(`[DEBUG] handlePedicleScrewChange triggered. Level: ${level}, Side: ${side}, Key: ${key}. Before Update:`, pedicleScrewSelections);
    setPedicleScrewSelections(prev => {
      const newState = {
        ...prev,
        [key]: !prev[key]
      };
      console.log(`[DEBUG] Updating pedicleScrewSelections. Key: ${key}, New Val: ${!prev[key]}. New Full State:`, newState);
      return newState;
    });
  };

  // Function to clear all interventions
  const handleClearInterventions = () => {
    setVertebralInterventions({});
    setIntervertebralInterventions({});
    setPedicleScrewSelections({});
    setHasInterventions(false);
  };

  // Handle spine correction changes
  const handleCorrectionChange = (level, value) => {
    // Allow empty string or numbers (including minus sign for flexion)
    if (value === '' || value === '-' || !isNaN(value)) {
      setSpineCorrections(prev => ({
        ...prev,
        [level]: value
      }));
    }
  };

  // Function to handle stage navigation
  const handleNextStage = () => {
    if (currentStage === 1 && hasInterventions) {
      setCurrentStage(2);
    }
  };

  const handlePreviousStage = () => {
    if (currentStage === 2) {
      setCurrentStage(1);
    }
  };

  // Fetch X-Ray Measurements for Initial Angles
  useEffect(() => {
    const fetchXRayData = async () => {
      try {
        const response = await api.get('/xray-measurements');
        if (response.status === 200) {
          const data = response.data;
          if (data.lat && data.lat.segmental_angles) {
            const angles = {};
            const originalAnglesMap = {};

            // Extract current (potentially adjusted) values for inputs
            Object.entries(data.lat.segmental_angles).forEach(([key, obj]) => {
              if (obj && typeof obj.value === 'number') {
                angles[key] = obj.value;
              }
            });

            // Extract ORIGINAL values for delta calculation baseline
            // Use 'original_lat' if available (added by backend), otherwise fallback to standard 'lat'
            const sourceForOriginal = data.original_lat || data.lat;
            if (sourceForOriginal && sourceForOriginal.segmental_angles) {
              Object.entries(sourceForOriginal.segmental_angles).forEach(([key, obj]) => {
                if (obj && typeof obj.value === 'number') {
                  originalAnglesMap[key] = obj.value;
                }
              });
            }

            setInitialAngles(originalAnglesMap);
            // Initialize spineCorrections with CURRENT/ADJUSTED values
            setSpineCorrections(prev => ({ ...prev, ...angles }));
          }
        }
      } catch (error) {
        console.error("Failed to fetch X-Ray measurements:", error);
      }
    };
    fetchXRayData();
  }, []);

  // Calculate Deltas for Visualizer (Target - Initial)
  const correctionDeltas = React.useMemo(() => {
    console.log("[PlanningPage] Recalculating Deltas. Inputs:", spineCorrections, "Baseline:", initialAngles);
    const deltas = {};
    Object.keys(spineCorrections).forEach(level => {
      const target = parseFloat(spineCorrections[level]);
      const initial = initialAngles[level] || 0;
      if (!isNaN(target)) {
        // Delta = Target - Initial
        // If Target (7) == Initial (7), Delta = 0 (No change visually)
        // If Target (17) > Initial (7), Delta = +10 (Extension)
        deltas[level] = target - initial;
      }
    });
    return deltas;
  }, [spineCorrections, initialAngles]);



  // Create comprehensive list of levels to display (vertebral + intervertebral)
  const allLevelsToDisplay = React.useMemo(() => {
    // Start with selected anatomical levels
    const vertebralLevels = [...anatomicalSelectedRows];

    // Sort vertebral levels by anatomical order
    const spineOrder = ['C1', 'C2', 'C3', 'C4', 'C5', 'C6', 'C7',
      'T1', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'T8', 'T9', 'T10', 'T11', 'T12',
      'L1', 'L2', 'L3', 'L4', 'L5', 'S1'];

    const sortedVertebral = vertebralLevels.sort((a, b) => {
      return spineOrder.indexOf(a) - spineOrder.indexOf(b);
    });

    // Generate intervertebral levels between consecutive selected vertebrae
    const intervertebralLevels = [];
    for (let i = 0; i < sortedVertebral.length - 1; i++) {
      const currentIndex = spineOrder.indexOf(sortedVertebral[i]);
      const nextIndex = spineOrder.indexOf(sortedVertebral[i + 1]);

      // Only add intervertebral level if vertebrae are consecutive
      if (nextIndex === currentIndex + 1) {
        intervertebralLevels.push(`${sortedVertebral[i]}-${sortedVertebral[i + 1]}`);
      }
    }

    return { vertebralLevels: sortedVertebral, intervertebralLevels };
  }, [anatomicalSelectedRows]);

  // --- Rod Length Calculation Logic ---

  // State for calculated rod lengths
  const [rodLengths, setRodLengths] = useState({});

  // Helper to get contiguous blocks of selected screws
  const getRodBlocks = useCallback(() => {
    const blocks = { R: [], L: [] };
    const spineOrder = ['C1', 'C2', 'C3', 'C4', 'C5', 'C6', 'C7',
      'T1', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'T8', 'T9', 'T10', 'T11', 'T12',
      'L1', 'L2', 'L3', 'L4', 'L5', 'S1'];

    // Get all selected levels sorted ANATOMICALLY
    const sortedLevels = [...anatomicalSelectedRows].sort((a, b) => {
      return spineOrder.indexOf(a) - spineOrder.indexOf(b);
    });

    if (sortedLevels.length < 2) return blocks;

    ['R', 'L'].forEach(side => {
      let currentBlock = null;

      // Identify start and end of rod implementation
      // Logic: A rod spans from the most superior screw to the most inferior screw.
      // We iterate through our sorted levels.

      let firstScrewIndex = -1;
      let lastScrewIndex = -1;

      // Find extent of screws
      sortedLevels.forEach((level, index) => {
        if (pedicleScrewSelections[`${level}_${side}`]) {
          if (firstScrewIndex === -1) firstScrewIndex = index;
          lastScrewIndex = index;
        }
      });

      // If we have at least two screws defining a span
      if (firstScrewIndex !== -1 && lastScrewIndex !== -1 && lastScrewIndex > firstScrewIndex) {
        // The rod covers everything from sortedLevels[firstScrewIndex] to sortedLevels[lastScrewIndex]
        const blockLevels = sortedLevels.slice(firstScrewIndex, lastScrewIndex + 1);

        blocks[side].push({
          startLevel: blockLevels[0],
          endLevel: blockLevels[blockLevels.length - 1],
          levels: blockLevels
        });
      }
    });

    return blocks;
  }, [anatomicalSelectedRows, pedicleScrewSelections]);

  // Calculate Length for a set of levels
  // Rod Length = Sum(Posterior Heights) + Sum(Disc Heights) + Buffer
  const calculateRodLength = useCallback((levels) => {
    let totalLength = 0;
    const BUFFER = 10; // 10mm buffer

    for (let i = 0; i < levels.length; i++) {
      const level = levels[i];
      const levelData = spineData.find(d => d.level === level);

      // Add Vertebral Posterior Height
      if (levelData && levelData.posteriorHeight) {
        totalLength += parseFloat(levelData.posteriorHeight) || 0;
      }

      // Add Disc Height (if not the last one)
      if (i < levels.length - 1) {
        // Find disc data for "Level-(Level+1)"
        // Since we don't have direct access to disc data in `spineData` easily without traversing,
        // we need to ensure `spineData` or another source has it.
        // Looking at fetchCTMeasurements, we only stored screw data.
        // We need to UPDATE fetchCTMeasurements to store posteriorHeight and discHeight.

        // Assuming spineData structure is updated:
        if (levelData && levelData.discHeightBelow) {
          totalLength += parseFloat(levelData.discHeightBelow) || 0;
        }
      }
    }

    return Math.ceil(totalLength + BUFFER);
  }, [spineData]);

  // Effect to recalculate all rod lengths when inputs change
  useEffect(() => {
    const blocks = getRodBlocks();
    const newRodLengths = {};

    ['R', 'L'].forEach(side => {
      blocks[side].forEach(block => {
        const length = calculateRodLength(block.levels);
        // Store length keyed by the block signature or just start-end?
        // Easier: Store based on the levels involved. 
        // We need to know for RENDER: "Am I part of a block? Which one?"

        // Make a unique key for the block to store its length
        const blockKey = `${side}_${block.startLevel}_${block.endLevel}`;
        newRodLengths[blockKey] = length;
      });
    });

    setRodLengths(newRodLengths);
  }, [getRodBlocks, calculateRodLength]);

  // END Rod Length Calculation Logic ---

  // Sample surgical plan data (would come from backend in production)
  const surgicalPlanData = {
    // Pre-operative measurements
    preOperative: {
      PI: 68,     // Pelvic Incidence
      PT: 32,     // Pelvic Tilt
      SS: 36,     // Sacral Slope
      LL: 28,     // Lumbar Lordosis
      TK: 38,     // Thoracic Kyphosis
      SVA: 84,    // Sagittal Vertical Axis
      T1PA: 26    // T1 Pelvic Angle
    },
    // Recommended post-operative targets
    recommended: {
      PT: 22,     // Recommended Pelvic Tilt
      LL: 58,     // Recommended Lumbar Lordosis
      TK: 48,     // Recommended Thoracic Kyphosis
      SVA: 40,    // Recommended Sagittal Vertical Axis
      T1PA: 14    // Recommended T1 Pelvic Angle
    },
    // Levels for intervention
    interventionLevels: [
      {
        level: "L2-L3",
        procedure: "TLIF",
        lordosisCorrection: "+6°"
      },
      {
        level: "L3-L4",
        procedure: "TLIF",
        lordosisCorrection: "+8°"
      },
      {
        level: "L4-L5",
        procedure: "TLIF",
        lordosisCorrection: "+8°"
      },
      {
        level: "L5-S1",
        procedure: "TLIF",
        lordosisCorrection: "+8°"
      }
    ],
    // Rod configuration
    rodConfiguration: {
      material: "Titanium",
      diameter: "5.5 mm",
      lordoticBendRegion: "L2-S1",
      bendAngle: "+30°"
    },
    // Expected post-operative measurements
    expectedOutcome: {
      PT: 22,     // Expected Pelvic Tilt
      LL: 58,     // Expected Lumbar Lordosis
      TK: 48,     // Expected Thoracic Kyphosis
      SVA: 40,    // Expected Sagittal Vertical Axis
      T1PA: 14    // Expected T1 Pelvic Angle
    },
    // Notes and specific considerations
    notes: "Patient has significant pelvic retroversion (high PT) with flat lumbar lordosis. Surgical plan aims to restore lordosis primarily in the lower lumbar spine while maintaining appropriate sagittal alignment."
  };

  // Check for cached measurements in sessionStorage
  const getCachedMeasurements = () => {
    const cached = sessionStorage.getItem('ctMeasurements');
    if (cached) {
      try {
        const parsedData = JSON.parse(cached);
        console.log('Loading measurements from cache');
        return parsedData;
      } catch (e) {
        console.error('Failed to parse cached measurements:', e);
      }
    }
    return null;
  };

  // Function to fetch CT measurements from the backend
  const fetchCTMeasurements = async (level) => {
    if (loadedLevels.has(level)) {
      console.log(`Using cached measurements for ${level}`);
      return;
    }

    setMeasurementsError(null);

    try {
      console.log(`Fetching CT measurements for ${level} from API`);
      const response = await api.get(`/ct-measurements/${level}`);

      if (response.status !== 200) {
        throw new Error(`Failed to fetch measurements for ${level}`);
      }

      const data = response.data;

      // Debug: Log the raw data structure
      console.log(`[DEBUG] Raw CT data for ${level}:`, data);
      console.log(`[DEBUG] Full data structure:`, JSON.stringify(data, null, 2));

      // Helper function to round down to nearest multiple of 5
      const roundDownToNearestFive = (value) => {
        const rounded = Math.floor(value / 5) * 5;
        console.log(`[DEBUG] Rounding ${value} down to ${rounded}`);
        return rounded;
      };

      // Helper function to format width with consistent spacing
      const formatWidth = (value) => {
        if (!value || value === 'N/A') return 'N/A';
        const formatted = value.toFixed(1);
        // Don't pad here, we'll handle alignment in display
        return formatted;
      };

      // Debug: Log the Pedicles structure
      console.log(`[DEBUG] Pedicles data:`, data?.Pedicles);
      console.log(`[DEBUG] Right pedicle:`, data?.Pedicles?.right);
      console.log(`[DEBUG] Left pedicle:`, data?.Pedicles?.left);

      // Process the measurements data - extract pedicle widths
      const pedicleWidthR = data?.Pedicles?.right?.["Measurement Data"]?.minor_2D_length;
      const pedicleWidthL = data?.Pedicles?.left?.["Measurement Data"]?.minor_2D_length;

      console.log(`[DEBUG] Pedicle widths - Right: ${pedicleWidthR}, Left: ${pedicleWidthL}`);

      // Debug: Log the Screw structure
      console.log(`[DEBUG] Right Screw data:`, data?.Pedicles?.right?.Screw);
      console.log(`[DEBUG] Right Screw Measurement Data:`, data?.Pedicles?.right?.Screw?.["Measurement Data"]);
      console.log(`[DEBUG] Left Screw Measurement Data:`, data?.Pedicles?.left?.Screw?.["Measurement Data"]);

      // Extract Landmark Data for visualization
      const rightLandmarks = data?.Pedicles?.right?.["Landmark Data"];
      const leftLandmarks = data?.Pedicles?.left?.["Landmark Data"];

      if (rightLandmarks || leftLandmarks) {
        setPedicleLandmarks(prev => ({
          ...prev,
          [level]: {
            right: rightLandmarks,
            left: leftLandmarks
          }
        }));
      }

      // Extract screw lengths from Measurement Data
      const screwLengthR = data?.Pedicles?.right?.Screw?.["Measurement Data"]?.length;
      const screwLengthL = data?.Pedicles?.left?.Screw?.["Measurement Data"]?.length;

      // Extract Vertebral Height (Posterior) and Disc Height (Inferior) for Rod Calc
      const posteriorHeight = data?.Vertebral_Body?.["Measurement Data"]?.posterior_height_distance;
      const vertebralDepth = data?.Vertebral_Body?.["Measurement Data"]?.vertebral_depth;
      const discHeightBelow = data?.Discs?.Inferior?.["Measurement Data"]?.disc_centroid_height;

      console.log(`[DEBUG] Extracted screw lengths - Right: ${screwLengthR}, Left: ${screwLengthL}`);
      console.log(`[DEBUG] Extracted heights - Posterior: ${posteriorHeight}, Disc Below: ${discHeightBelow}, Vert Depth: ${vertebralDepth}`);

      const processedLevelData = {
        level,
        pedicleWidthR: pedicleWidthR ? formatWidth(pedicleWidthR) : 'N/A',
        pedicleWidthL: pedicleWidthL ? formatWidth(pedicleWidthL) : 'N/A',
        screwLengthR: screwLengthR ? roundDownToNearestFive(screwLengthR) : 'N/A',
        screwLengthL: screwLengthL ? roundDownToNearestFive(screwLengthL) : 'N/A',
        posteriorHeight: posteriorHeight || 0,
        vertebralDepth: vertebralDepth || 0,
        discHeightBelow: discHeightBelow || 0,
        isLoading: false,
        hasError: false
      };

      // Update spine data with the new measurements
      setSpineData(prevData =>
        prevData.map(item =>
          item.level === level ? processedLevelData : item
        )
      );

      // Mark this level as loaded
      setLoadedLevels(prev => new Set([...prev, level]));

    } catch (error) {
      console.error('Error fetching CT measurements:', error);
      setMeasurementsError(error.message);

      // Update spine data to show error state
      setSpineData(prevData =>
        prevData.map(item =>
          item.level === level ? {
            ...item,
            isLoading: false,
            hasError: true,
            pedicleWidthR: 'Error',
            pedicleWidthL: 'Error'
          } : item
        )
      );
    }
  };

  // Fetch measurements for selected levels only
  // Fetch measurements for selected levels only (renamed to handleAnalyze for consistency)
  const handleAnalyze = useCallback(async () => {
    if (anatomicalSelectedRows.length === 0) return;

    setMeasurementsLoading(true);

    // Check for cached data first
    const cachedData = getCachedMeasurements();
    if (cachedData && cachedData.measurements) {
      cachedData.measurements.forEach(cached => {
        if (anatomicalSelectedRows.includes(cached.level)) {
          setSpineData(prevData =>
            prevData.map(item =>
              item.level === cached.level ? {
                level: cached.level,
                pedicleWidthR: cached.pedicleWidthR,
                pedicleWidthL: cached.pedicleWidthL,
                isLoading: false,
                hasError: false
              } : item
            )
          );
          setLoadedLevels(prev => new Set([...prev, cached.level]));
        }
      });
    }

    // Set loading state for levels that need to be fetched
    const levelsToFetch = anatomicalSelectedRows.filter(level => !loadedLevels.has(level));

    // Set loading state for all levels that will be fetched
    if (levelsToFetch.length > 0) {
      setSpineData(prevData =>
        prevData.map(item =>
          levelsToFetch.includes(item.level)
            ? { ...item, isLoading: true }
            : item
        )
      );
    }

    // Fetch any missing levels
    const promises = levelsToFetch.map(level => fetchCTMeasurements(level));

    await Promise.all(promises);
    setMeasurementsLoading(false);
  }, [anatomicalSelectedRows, loadedLevels, fetchCTMeasurements]);

  // Load instrumentation data
  const loadInstrumentationData = useCallback(async () => {
    setInstrumentationLoading(true);
    try {
      // Load pedicle screws data
      const screwsResponse = await fetch('/instrumentation_data/Pedicle_Screws.csv');
      const screwsText = await screwsResponse.text();
      const screwsResult = Papa.parse(screwsText, { header: true, dynamicTyping: true });
      setPedicleScrewsData(screwsResult.data);

      console.log('Loaded pedicle screws:', screwsResult.data.length);
    } catch (error) {
      console.error('Error loading instrumentation data:', error);
    } finally {
      setInstrumentationLoading(false);
    }
  }, []);

  // Initialize data on mount and when selections change
  useEffect(() => {
    loadInstrumentationData();
  }, [loadInstrumentationData]);

  useEffect(() => {
    if (anatomicalSelectedRows.length > 0) {
      handleAnalyze();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anatomicalSelectedRows]);

  // Find best matching pedicle screw
  const findBestPedicleScrew = (targetDiameter, targetLength) => {
    if (!pedicleScrewsData.length || !targetDiameter || !targetLength) return null;

    // Convert to numbers
    const diameter = parseFloat(targetDiameter);
    const length = parseFloat(targetLength);

    if (isNaN(diameter) || isNaN(length)) return null;

    // Filter screws by diameter (must be <= target diameter)
    const validScrews = pedicleScrewsData.filter(screw => {
      const screwDiameter = parseFloat(screw.Diameter);
      return !isNaN(screwDiameter) && screwDiameter <= diameter;
    });

    if (validScrews.length === 0) return null;

    // Find the best match: closest diameter, then closest length
    let bestMatch = null;
    let bestScore = Infinity;

    validScrews.forEach(screw => {
      const screwDiameter = parseFloat(screw.Diameter);
      const screwLength = parseFloat(screw.Length);

      if (isNaN(screwLength)) return;

      // Score based on diameter difference (weighted more) and length difference
      const diameterDiff = diameter - screwDiameter;
      const lengthDiff = Math.abs(length - screwLength);
      const score = diameterDiff * 10 + lengthDiff; // Weight diameter more heavily

      if (score < bestScore) {
        bestScore = score;
        bestMatch = screw;
      }
    });

    return bestMatch;
  };

  // Update matched screws when spine data changes
  useEffect(() => {
    if (!pedicleScrewsData.length) return;

    const newMatchedScrews = {};

    spineData.forEach(levelData => {
      if (levelData.pedicleWidthR !== 'N/A' && levelData.screwLengthR !== 'N/A') {
        const match = findBestPedicleScrew(levelData.pedicleWidthR, levelData.screwLengthR);
        if (match) {
          newMatchedScrews[`${levelData.level}_R`] = match;
        }
      }

      if (levelData.pedicleWidthL !== 'N/A' && levelData.screwLengthL !== 'N/A') {
        const match = findBestPedicleScrew(levelData.pedicleWidthL, levelData.screwLengthL);
        if (match) {
          newMatchedScrews[`${levelData.level}_L`] = match;
        }
      }
    });

    setMatchedScrews(newMatchedScrews);
    console.log('Updated matched screws:', newMatchedScrews);
  }, [spineData, pedicleScrewsData]);


  // Fetch spine images when component mounts
  useEffect(() => {
    fetchSpineImages();
  }, []);



  // Function to fetch spine images from the backend
  const fetchSpineImages = async () => {
    setImagesLoading(true);
    setImageError(null);

    try {
      // Check if API is available
      await api.get(`${API_URL}/root`);
      console.log('API is available, fetching spine images...');

      // Create URLs for the original and translated spine STL images
      const originalSpineUrl = `${API_URL}/images/spine/original`;
      const translatedSpineUrl = `${API_URL}/images/spine/translated`;

      console.log(`Fetching original spine from: ${originalSpineUrl}`);
      console.log(`Fetching translated spine from: ${translatedSpineUrl}`);

      // Set direct URLs to the images
      setOriginalSpineImage(originalSpineUrl);
      setTranslatedSpineImage(translatedSpineUrl);
    } catch (error) {
      console.error('Error setting up spine images:', error);
      setImageError('Failed to load spine images');
    } finally {
      setImagesLoading(false);
    }
  };

  const handleBackToAlignment = () => {
    console.log('Navigating back to Alignment Page...');
    navigate('/alignment');
  };

  const handleRegionToggle = (region) => {
    // Check if this would result in all regions being hidden
    const wouldAllBeHidden = Object.entries(selectedRegions)
      .filter(([key]) => key !== region) // Exclude the region we're toggling
      .every(([_, isSelected]) => !isSelected) && selectedRegions[region]; // All others are false and this one is true (about to be toggled off)

    // Only allow toggling if it won't hide all regions
    if (!wouldAllBeHidden) {
      setSelectedRegions(prev => ({
        ...prev,
        [region]: !prev[region]
      }));
    }
  };

  // Current Alignment Spine Viewer Component removed (using shared SpineModelViewer)


  return (
    <div className="pp-root">
      <style>{`
        .planning-select option {
          background-color: #1c1f24 !important;
          color: rgba(255, 255, 255, 0.94) !important;
          padding: 8px 12px;
        }
        .planning-select option:hover {
          background-color: #24282e !important;
        }

        /* --- PlanningPage layout (page-scoped) --- */
        .pp-root { flex: 1; min-height: 0; display: flex; flex-direction: column; overflow: hidden; }
        .pp-dashboard { flex: 1; min-height: 0; display: flex; flex-direction: column; gap: var(--space-3); padding: var(--space-3) clamp(var(--space-3), 2vw, var(--space-5)); overflow: auto; }
        .pp-top { flex: 0 0 auto; height: 52%; min-height: 350px; max-height: 460px; display: flex; flex-direction: column; }
        .pp-top-row { flex: 1; min-height: 0; display: flex; gap: var(--space-3); }
        .pp-module { flex: 1; min-width: 0; min-height: 0; display: flex; flex-direction: column; }
        .pp-module__header { flex: 0 0 auto; display: flex; align-items: center; justify-content: center; padding: 9px 14px; border-bottom: 1px solid var(--line-subtle); font-size: 0.85rem; font-weight: 600; letter-spacing: -0.01em; color: var(--text-primary); }
        .pp-viewer { flex: 1; min-height: 0; position: relative; overflow: hidden; background: var(--bg-viewer); }
        .pp-bottom { flex: 1 1 auto; min-height: 300px; position: relative; overflow: hidden; }
        .pp-stagebox { position: relative; width: 100%; height: 100%; overflow: hidden; }
        .pp-stage { position: absolute; inset: 0; display: flex; flex-direction: column; transition: transform var(--dur-slow) var(--ease-spring), opacity var(--dur-base) var(--ease-spring); }
        .pp-stage1 { display: flex; gap: var(--space-4); height: 100%; align-items: stretch; }
        .pp-panel { flex: 1; min-width: 0; min-height: 0; display: flex; flex-direction: column; }
        .pp-panel__header { flex: 0 0 auto; display: flex; align-items: center; justify-content: space-between; gap: var(--space-3); padding: 8px 14px; border-bottom: 1px solid var(--line-subtle); font-size: 0.9rem; font-weight: 600; letter-spacing: -0.01em; color: var(--text-primary); }
        .pp-panel__title { display: inline-flex; align-items: center; gap: 10px; }
        .pp-clear { height: 28px; min-width: 28px; padding: 0 6px; }

        /* Stage 1 interventions table */
        .pp-t1 { flex: 1; min-height: 0; display: flex; flex-direction: column; }
        .pp-t1-scroll, .pp-t2-scroll { flex: 1; min-height: 0; overflow: auto; }
        /* Shared-width wrapper: every row sizes against the widest row instead
           of its own fit-content, so columns stay aligned once real data gives
           rows different natural widths. */
        .pp-table-inner { display: inline-block; min-width: 100%; vertical-align: top; }
        .pp-t1-head, .pp-t2-head { display: flex; position: sticky; top: 0; z-index: 15; background: var(--bg-elevated); border-bottom: 1px solid var(--line-default); }
        .pp-t1-head .pp-cell { min-height: 34px; }
        .pp-t1-head .pp-cell, .pp-t2-head .pp-cell2 { font-size: 0.7rem; font-weight: 600; letter-spacing: 0.05em; text-transform: uppercase; color: var(--text-tertiary); }
        .pp-t1-row { display: flex; border-bottom: 1px solid var(--line-subtle); transition: background var(--dur-instant) ease; }
        .pp-t1-row:hover { background: rgba(255, 255, 255, 0.03); }
        .pp-cell { display: flex; align-items: center; justify-content: center; min-height: 44px; padding: 4px 10px; border-right: 1px solid var(--line-subtle); min-width: 0; font-size: 0.84rem; }
        .pp-cell:last-child { border-right: none; }
        .pp-c-level { flex: 0 0 64px; min-width: 64px; font-weight: 600; }
        .pp-c-ped { flex: 1.8 1 300px; min-width: 300px; }
        .pp-c-vert { flex: 1 1 150px; min-width: 150px; }
        .pp-c-disc { flex: 1.3 1 190px; min-width: 190px; gap: 10px; }
        .pp-c-ivd { flex: 1 1 150px; min-width: 150px; }
        .pp-pedgroup { display: flex; align-items: center; gap: var(--space-4); width: 100%; justify-content: center; }
        .pp-pedside { display: flex; align-items: center; gap: 6px; min-width: 132px; justify-content: flex-start; }
        .pp-check { width: 15px; height: 15px; accent-color: var(--accent); cursor: pointer; flex-shrink: 0; }
        .pp-sidelabel { font-size: 0.74rem; font-weight: 600; color: var(--text-secondary); min-width: 14px; }
        .pp-ped-value { font-size: 0.8rem; font-family: var(--font-mono); color: var(--text-primary); white-space: pre; }
        .pp-ped-value--warn { color: var(--danger); font-weight: 600; }
        .pp-vr { color: var(--text-disabled); font-size: 0.75rem; }
        .pp-discrow { display: flex; align-items: center; gap: 10px; }
        .pp-disclabel { font-size: 0.82rem; font-weight: 600; color: var(--teal); min-width: 48px; text-align: right; }
        .pp-select { height: 30px; font-size: 0.78rem; padding: 0 26px 0 10px; border-radius: var(--radius-xs); background-position: right 9px center; width: 100%; max-width: 180px; }

        /* Degree-correction input */
        .pp-angle { position: relative; display: flex; align-items: center; width: 56px; height: 30px; background: var(--bg-input); border: 1px solid var(--line-default); border-radius: var(--radius-xs); transition: border-color var(--dur-fast) var(--ease-spring), box-shadow var(--dur-fast) var(--ease-spring); }
        .pp-angle--set { border-color: var(--accent); }
        .pp-angle:focus-within { border-color: var(--accent); box-shadow: 0 0 0 3px rgba(79, 156, 240, 0.18); }
        .pp-angle-input { width: 100%; height: 100%; background: transparent; border: none; outline: none; text-align: center; padding: 0 14px 0 4px; font-size: 0.85rem; font-weight: 500; color: var(--text-primary); }
        .pp-angle-input::placeholder { color: var(--text-tertiary); }
        .pp-angle-suffix { position: absolute; right: 7px; top: 50%; transform: translateY(-56%); pointer-events: none; user-select: none; font-size: 0.85rem; color: var(--text-secondary); }

        /* Stage navigation — quiet, instant press feedback, no oversized chrome */
        .pp-arrowbox { flex: 0 0 clamp(170px, 22vw, 280px); display: flex; flex-direction: column; align-items: center; justify-content: center; gap: var(--space-3); }
        .pp-stepnum { font-size: 0.72rem; font-weight: 600; letter-spacing: 0.05em; text-transform: uppercase; color: var(--text-tertiary); }
        .pp-next { display: inline-flex; align-items: center; gap: 7px; height: 44px; padding: 0 12px 0 22px; border: none; border-radius: 22px; background: var(--accent); color: #fff; font-size: 0.92rem; font-weight: 600; letter-spacing: -0.01em; cursor: pointer; transition: background 120ms ease, transform 120ms cubic-bezier(0.2, 0.8, 0.4, 1), opacity 120ms ease; }
        .pp-next svg { transition: transform 200ms cubic-bezier(0.2, 0.8, 0.4, 1); }
        .pp-next:hover:not(:disabled) { background: var(--accent-hover); }
        .pp-next:hover:not(:disabled) svg { transform: translateX(2px); }
        .pp-next:active:not(:disabled) { background: var(--accent-active); transform: scale(0.97); }
        .pp-next:disabled { background: var(--accent-soft); color: var(--accent); opacity: 0.6; cursor: not-allowed; }
        .pp-arrow-label { font-size: 0.8rem; color: var(--text-tertiary); text-align: center; max-width: 190px; line-height: 1.4; }

        /* Stage 2 hardware table */
        .pp-stage2 { display: flex; gap: var(--space-2); height: 100%; min-height: 0; }
        /* Hover-reveal back affordance: a faint chevron chip that brightens and
           grows a "Back" label on hover/focus. Expansion overlays the gutter
           edge briefly (z-index) instead of reflowing the table. */
        .pp-backrail { flex: 0 0 40px; display: flex; align-items: center; justify-content: center; }
        .pp-back { position: relative; z-index: 20; display: inline-flex; align-items: center; gap: 5px; height: 36px; padding: 0 9px; border: none; border-radius: 18px; background: rgba(255, 255, 255, 0.05); color: var(--text-tertiary); opacity: 0.6; cursor: pointer; font-size: 0.82rem; font-weight: 600; letter-spacing: -0.01em; backdrop-filter: blur(12px); -webkit-backdrop-filter: blur(12px); transition: opacity 150ms ease, background 150ms ease, color 150ms ease, transform 120ms cubic-bezier(0.2, 0.8, 0.4, 1); }
        .pp-back__label { max-width: 0; opacity: 0; overflow: hidden; white-space: nowrap; transition: max-width 220ms cubic-bezier(0.2, 0.8, 0.4, 1), opacity 160ms ease; }
        .pp-back:hover, .pp-back:focus-visible { opacity: 1; background: rgba(255, 255, 255, 0.1); color: var(--text-primary); }
        .pp-back:hover .pp-back__label, .pp-back:focus-visible .pp-back__label { max-width: 60px; opacity: 1; }
        .pp-back:active { transform: scale(0.96); }
        @media (prefers-reduced-motion: reduce) {
          .pp-back, .pp-back__label, .pp-next, .pp-next svg { transition: none; }
        }
        .pp-t2-wrap { flex: 1; min-width: 0; display: flex; }
        .pp-t2 { flex: 1; min-width: 0; min-height: 0; display: flex; flex-direction: column; }
        /* Columns are sized to always fit the panel width — every cell has
           min-width:0 and truncating content, so nothing can force the table
           into horizontal scrolling. Rows grow in height instead. */
        .pp-t2 .pp-table-inner { display: block; }
        .pp-t2-row { display: flex; min-height: 45px; align-items: stretch; border-bottom: 1px solid var(--line-subtle); transition: background var(--dur-instant) ease; }
        .pp-t2-row:hover { background: rgba(255, 255, 255, 0.03); }
        .pp-t2-row--disc { background: rgba(255, 255, 255, 0.02); }
        .pp-cell2 { display: flex; align-items: center; justify-content: center; min-height: 45px; padding: 4px 8px; border-right: 1px solid var(--line-subtle); min-width: 0; font-size: 0.84rem; }
        .pp-cell2:last-child { border-right: none; }
        .pp-t2-head .pp-cell2 { white-space: normal; text-align: center; line-height: 1.3; }
        .pp-c2-level { flex: 0 0 48px; font-weight: 600; }
        .pp-c2-cages { flex: 1.2 1 120px; min-width: 104px; }
        .pp-c2-screws { flex: 1.2 1 132px; min-width: 116px; }
        .pp-c2-rods { flex: 0.7 1 84px; min-width: 72px; padding-top: 0; padding-bottom: 0; }
        .pp-c2-plates { flex: 0 0 64px; }
        .pp-c2-pscrews { flex: 0 0 96px; }
        .pp-c2-disc { flex: 0 0 64px; font-weight: 600; color: var(--teal); }
        .pp-c2-device { flex: 1.2 1 120px; min-width: 104px; }
        .pp-c2-dscrews { flex: 0 0 72px; }
        .pp-sgroup { display: flex; flex-direction: column; gap: 3px; width: 100%; padding: 5px 0; min-width: 0; }
        .pp-sside { display: flex; align-items: center; gap: 6px; width: 100%; min-width: 0; justify-content: flex-start; }
        .pp-screw-badge { display: inline-block; max-width: 100%; padding: 4px 9px; border-radius: var(--radius-full); background: var(--accent-soft); color: var(--accent-hover); font-size: 0.7rem; font-weight: 600; font-family: var(--font-mono); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; cursor: default; }
        .pp-na { color: var(--text-tertiary); font-size: 0.78rem; font-style: italic; }
        .pp-mmbox { display: inline-flex; align-items: center; gap: 4px; background: var(--bg-input); border: 1px solid var(--line-default); border-radius: var(--radius-xs); padding: 0 8px; height: 30px; transition: border-color var(--dur-fast) var(--ease-spring), box-shadow var(--dur-fast) var(--ease-spring); }
        .pp-mmbox:focus-within { border-color: var(--accent); box-shadow: 0 0 0 3px rgba(79, 156, 240, 0.18); }
        .pp-mini { background: transparent; border: none; outline: none; width: 34px; text-align: center; font-size: 0.85rem; font-weight: 500; color: var(--text-primary); }
        .pp-mini::placeholder { color: var(--text-tertiary); }
        .pp-unit { color: var(--text-secondary); font-size: 0.8rem; user-select: none; }
      `}</style>
      <div className="pp-dashboard">
        {/* Top Section - Spine Alignment Views */}
        <div className="pp-top">
          {/* Current and Projected Spine Alignment */}
          <div className="pp-top-row">
            {/* Current Alignment */}
            <div className="card card--flush pp-module">
              <div className="pp-module__header">Current Alignment</div>
              <div className="pp-viewer">
                <SpineModelViewer
                  selectedLevel={null}
                  selectedLevels={[]}
                  concerningLevels={concerningRows}
                  herniatedLevels={herniatedRows}
                  spineOptions={selectedRegions}
                  showInfoPanel={false}
                  viewerId="current"
                />
                {/* Debug info */}
                <div className="viewer__hud" style={{ top: 10, right: 10, textAlign: 'right' }}>
                  {concerningRows.length > 0 && <div>Stenosis: {concerningRows.join(', ')}</div>}
                  {herniatedRows.filter(h => !concerningRows.includes(h)).length > 0 &&
                    <div>Herniated: {herniatedRows.filter(h => !concerningRows.includes(h)).join(', ')}</div>
                  }
                </div>
              </div>
            </div>

            {/* Projected Alignment */}
            <div className="card card--flush pp-module">
              <div className="pp-module__header">Projected Alignment</div>
              <div className="pp-viewer">
                <SpineModelViewer
                  selectedLevel={null}
                  selectedLevels={anatomicalSelectedRows}
                  concerningLevels={anatomicalSelectedRows} // Show interventions as concerning/highlighted
                  spineOptions={selectedRegions}
                  corrections={correctionDeltas}
                  distractions={spineDistractions} // Pass distraction data
                  showInfoPanel={false} // Hide info panel for projected view
                  showHardware={currentStage === 2}
                  viewerId="projected"
                  vertebralMetrics={(() => {
                    const metrics = {};
                    spineData.forEach(d => {
                      if (d.vertebralDepth) metrics[d.level] = { vertebralDepth: d.vertebralDepth };
                    });
                    return metrics;
                  })()}
                  pedicleScrews={(() => {
                    // Filter and construct pedicle screws data for viewer
                    // Always log current stage for debugging

                    const screws = {};
                    // console.log("[DEBUG] Pedicle Screws Logic:", { selectionsKeys: Object.keys(pedicleScrewSelections), landmarksKeys: Object.keys(pedicleLandmarks), screwsObj: screws });
                    Object.entries(pedicleScrewSelections).forEach(([key, isSelected]) => {
                      if (!isSelected) return;
                      // key is like "L4_right" or "L4_left" -> wait, handlePedicleScrewChange uses "L4_right" ? 
                      // Let's check handlePedicleScrewChange. It uses `L4_R` or `L4_Right`? 
                      // "handlePedicleScrewChange(level, side)" -> set `level_side`
                      // It seems side is passed as 'R' or 'L' based on UI checkboxes? 
                      // Let's look at the UI. "pedicleCheckbox" calls handlePedicleScrewChange level, 'R'/'L' likely.

                      const [level, sideShort] = key.split('_');
                      // sideShort is 'R' or 'L' usually.
                      const side = sideShort === 'R' ? 'right' : 'left';

                      if (!screws[level]) screws[level] = {};

                      // Get matched screw specs
                      const matchKey = `${level}_${sideShort}`;
                      const screwSpec = matchedScrews[matchKey] || { Diameter: 5.5, Length: 45 };

                      // Get landmarks
                      const landmarks = pedicleLandmarks[level]?.[side];

                      if (landmarks) {
                        screws[level][side] = {
                          diameter: screwSpec.Diameter,
                          length: screwSpec.Length,
                          landmarks: landmarks
                        };
                      }
                    });
                    return screws;
                  })()}
                  interbodyDevices={(() => {
                    // Filter valid selections
                    const devices = {};
                    Object.entries(interbodyDeviceSelections).forEach(([level, device]) => {
                      if (device && intervertebralInterventions[level]) { // Only if still valid/selected
                        devices[level] = device;
                      }
                    });
                    return devices;
                  })()}
                />
                {/* Debug info */}
                <div className="viewer__hud" style={{ top: 10, right: 10 }}>
                  Interventions: {anatomicalSelectedRows.join(', ') || 'None'}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Section - Two Stage Implementation */}
        <div className="pp-bottom">
          <div className="pp-stagebox">
            {/* Stage 1: Intervention Planning */}
            <div className="pp-stage" inert={currentStage !== 1 || undefined} style={{
              transform: `translateX(${currentStage === 1 ? '0' : '-100%'})`,
              opacity: currentStage === 1 ? 1 : 0,
              pointerEvents: currentStage === 1 ? 'auto' : 'none'
            }}>
              <div className="pp-stage1">
                {/* Left spacer to align with Current Alignment */}
                <div style={{ display: 'none' }}></div>

                {/* Interventions Panel - aligned with Current Alignment */}
                <div className="card card--flush pp-panel">
                  <div className="pp-panel__header">
                    <span className="pp-panel__title">
                      Interventions
                      {measurementsLoading && <span className="spinner spinner--sm" aria-hidden="true" />}
                    </span>
                    {/* Clear Interventions Button */}
                    <button
                      type="button"
                      className="btn btn--danger btn--sm pp-clear"
                      onClick={handleClearInterventions}
                      disabled={!hasInterventions}
                      title="Clear all interventions"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M3 6H5H21M8 6V4C8 3.44772 8.44772 3 9 3H15C15.5523 3 16 3.44772 16 4V6M19 6V20C19 20.5523 18.5523 21 18 21H6C5.44772 21 5 20.5523 5 20V6H19Z"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M10 11V17M14 11V17"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>

                  <div className="pp-t1">
                    {/* Table Content (header is sticky inside the scroller) */}
                    <div className="pp-t1-scroll">
                    <div className="pp-table-inner">
                      {/* Table Header */}
                      <div className="pp-t1-head">
                        <div className="pp-cell pp-c-level">Level</div>
                        <div className="pp-cell pp-c-ped">Pedicle Screw Info</div>
                        <div className="pp-cell pp-c-vert">Vertebral</div>
                        <div className="pp-cell pp-c-disc">Disc</div>
                        <div className="pp-cell pp-c-ivd">Intervertebral</div>
                      </div>
                      {selectedVertebralLevels.map(level => {
                        const levelData = spineData.find(item => item.level === level) || {};
                        const interLevel = intervertebralLevels.find(il => il.startsWith(level + '-'));

                        return (
                          <div key={level} className="pp-t1-row">
                            <div className="pp-cell pp-c-level">{level}</div>
                            <div className="pp-cell pp-c-ped">
                              <div className="pp-pedgroup">
                                <div className="pp-pedside">
                                  <input
                                    type="checkbox"
                                    className="pp-check"
                                    checked={pedicleScrewSelections[`${level}_R`] || false}
                                    onChange={() => handlePedicleScrewChange(level, 'R')}
                                  />
                                  <span className="pp-sidelabel">R:</span>
                                  <span className={`pp-ped-value${isHighlighted(level, 'pedicleWidthR', levelData.pedicleWidthR) ? ' pp-ped-value--warn' : ''}`}>
                                    {levelData.isLoading
                                      ? '...'
                                      : levelData.pedicleWidthR !== 'N/A' && levelData.screwLengthR !== 'N/A'
                                        ? (
                                          <>
                                            <span style={{ display: 'inline-block', width: '30px', textAlign: 'right' }}>
                                              {levelData.pedicleWidthR}
                                            </span>
                                            <span> x {levelData.screwLengthR}mm</span>
                                          </>
                                        )
                                        : 'N/A'}
                                  </span>
                                </div>
                                <div className="pp-vr">|</div>
                                <div className="pp-pedside">
                                  <input
                                    type="checkbox"
                                    className="pp-check"
                                    checked={pedicleScrewSelections[`${level}_L`] || false}
                                    onChange={() => handlePedicleScrewChange(level, 'L')}
                                  />
                                  <span className="pp-sidelabel">L:</span>
                                  <span className={`pp-ped-value${isHighlighted(level, 'pedicleWidthL', levelData.pedicleWidthL) ? ' pp-ped-value--warn' : ''}`}>
                                    {levelData.isLoading
                                      ? '...'
                                      : levelData.pedicleWidthL !== 'N/A' && levelData.screwLengthL !== 'N/A'
                                        ? (
                                          <>
                                            <span style={{ display: 'inline-block', width: '30px', textAlign: 'right' }}>
                                              {levelData.pedicleWidthL}
                                            </span>
                                            <span> x {levelData.screwLengthL}mm</span>
                                          </>
                                        )
                                        : 'N/A'}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="pp-cell pp-c-vert">
                              <select
                                className="select pp-select planning-select"
                                value={vertebralInterventions[level] || ''}
                                onChange={(e) => handleVertebralInterventionChange(level, e.target.value)}
                              >
                                <option value="">Select Intervention</option>
                                <option value="Partial Corpectomy">Partial Corpectomy</option>
                                <option value="Full Corpectomy">Full Corpectomy</option>
                                <option value="Partial w/Plate">Partial w/Plate</option>
                                <option value="Full w/Plate">Full w/Plate</option>
                              </select>
                            </div>
                            <div className="pp-cell pp-c-disc">
                              {interLevel && (
                                <div className="pp-discrow">
                                  <div className="pp-disclabel">{interLevel}</div>
                                  <div className={`pp-angle${spineCorrections[interLevel] ? ' pp-angle--set' : ''}`}>
                                    <input
                                      type="text"
                                      className="pp-angle-input"
                                      placeholder="0"
                                      value={spineCorrections[interLevel] || ''}
                                      onChange={(e) => handleCorrectionChange(interLevel, e.target.value)}
                                    />
                                    <span className="pp-angle-suffix">&deg;</span>
                                  </div>
                                </div>
                              )}
                            </div>
                            <div className="pp-cell pp-c-ivd">
                              {interLevel && (
                                <select
                                  className="select pp-select planning-select"
                                  value={intervertebralInterventions[interLevel] || ''}
                                  onChange={(e) => handleIntervertebralInterventionChange(interLevel, e.target.value)}
                                >
                                  <option value="">Select Procedure</option>
                                  <option value="PLIF">PLIF</option>
                                  <option value="ALIF">ALIF</option>
                                  <option value="TLIF">TLIF</option>
                                  <option value="XLIF/DLIF">XLIF/DLIF</option>
                                </select>
                              )}
                            </div>
                          </div>
                        );
                      })}
                      {selectedVertebralLevels.length === 0 && (
                        <div className="empty">
                          <div className="empty__title">No spinal levels selected</div>
                          <div className="empty__sub">Select vertebral levels on the Measurements step to begin intervention planning.</div>
                        </div>
                      )}
                    </div>
                    </div>
                  </div>
                </div>

                {/* Stage navigation */}
                <div className="pp-arrowbox">
                  <div className="pp-stepnum">Step 1 of 2</div>
                  <button
                    type="button"
                    className="pp-next"
                    onClick={handleNextStage}
                    disabled={!hasInterventions}
                  >
                    <span>Continue</span>
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                    >
                      <path
                        d="M9 6L15 12L9 18"
                        stroke="currentColor"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                  {!hasInterventions && (
                    <div className="pp-arrow-label">Select interventions to continue</div>
                  )}
                </div>
              </div>
            </div>

            {/* Stage 2: Pre-op Planning */}
            <div className="pp-stage" inert={currentStage !== 2 || undefined} style={{
              transform: `translateX(${currentStage === 2 ? '0' : '100%'})`,
              opacity: currentStage === 2 ? 1 : 0,
              pointerEvents: currentStage === 2 ? 'auto' : 'none'
            }}>
              <div className="pp-stage2">
                {/* Quiet back affordance: a faint chevron chip in the gutter
                    that expands into a labeled pill on hover/focus */}
                <div className="pp-backrail">
                  <button
                    type="button"
                    className="pp-back"
                    onClick={handlePreviousStage}
                    aria-label="Back to Interventions"
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                    >
                      <path
                        d="M15 18L9 12L15 6"
                        stroke="currentColor"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <span className="pp-back__label">Back</span>
                  </button>
                </div>

                {/* Surgical Plan Table - with left margin to match interventions panel */}
                <div className="pp-t2-wrap">
                  <div className="card card--flush pp-t2">
                    {/* Header is sticky inside the scroller so it tracks horizontal scroll */}
                    <div className="pp-t2-scroll">
                    <div className="pp-table-inner">
                      <div className="pp-t2-head">
                        <div className="pp-cell2 pp-c2-level">Level</div>
                        <div className="pp-cell2 pp-c2-cages">Cages</div>
                        <div className="pp-cell2 pp-c2-screws">Pedicle Screws</div>
                        <div className="pp-cell2 pp-c2-rods">Rods</div>
                        <div className="pp-cell2 pp-c2-plates">Plates</div>
                        <div className="pp-cell2 pp-c2-pscrews">Plate Screws</div>
                        <div className="pp-cell2 pp-c2-disc">Disc</div>
                        <div className="pp-cell2 pp-c2-device">Interbody Device</div>
                        <div className="pp-cell2 pp-c2-dscrews">Device Screws</div>
                      </div>
                      {/* Vertebral Levels */}
                      {allLevelsToDisplay.vertebralLevels.map((level, index) => {
                        const hasInterventebralAfter = allLevelsToDisplay.intervertebralLevels.some(il => il.startsWith(level + '-'));

                        return (
                          <React.Fragment key={level}>
                            {/* Vertebral Level Row */}
                            <div className="pp-t2-row">
                              <div className="pp-cell2 pp-c2-level">{level}</div>
                              <div className="pp-cell2 pp-c2-cages">
                                <SearchableDropdown
                                  apiEndpoint={`${API_URL}/instrumentation/cages`}
                                  labelKey="Label"
                                  valueKey="Label"
                                  placeholder={
                                    cageSelections[level]
                                      || (vertebralInterventions[level] ? "Select Cage" : "Select Intervention First")
                                  }
                                  disabled={!vertebralInterventions[level]}
                                  queryParams={{
                                    section: level.startsWith('C') ? 'Cervical' : 'Lumbar'
                                  }}
                                  onChange={(value) => {
                                    setCageSelections(prev => ({ ...prev, [level]: value }));
                                  }}
                                />
                              </div>
                              <div className="pp-cell2 pp-c2-screws">
                                <div className="pp-sgroup">
                                  {['R', 'L'].map(side => (
                                    <div className="pp-sside" key={side}>
                                      <span className="pp-sidelabel">{side}:</span>
                                      {(() => {
                                        const matchedScrew = matchedScrews[`${level}_${side}`];
                                        const isSelected = pedicleScrewSelections[`${level}_${side}`];
                                        if (!isSelected) {
                                          return <div className="pp-na">N/A</div>;
                                        }
                                        if (matchedScrew) {
                                          return (
                                            <div className="pp-screw-badge" title={matchedScrew.Label}>
                                              {matchedScrew.Label}
                                            </div>
                                          );
                                        }
                                        // Catalog loaded but nothing fits (e.g. pedicle
                                        // narrower than every screw) — say so instead of
                                        // showing "Loading..." forever.
                                        if (pedicleScrewsData.length > 0) {
                                          return (
                                            <div className="pp-na" title="No catalog screw fits the measured pedicle dimensions">
                                              No match
                                            </div>
                                          );
                                        }
                                        return <div className="pp-na">Loading...</div>;
                                      })()}
                                    </div>
                                  ))}
                                </div>
                              </div>
                              <div className="pp-cell2 pp-c2-rods">
                                {(() => {
                                  // --- Rod Rendering Logic (Vertebral Row) ---

                                  const getRodInfo = (side) => {
                                    const blocks = getRodBlocks()[side];
                                    const block = blocks.find(b => b.levels.includes(level));
                                    if (!block) return null;

                                    const blockLen = block.levels.length;
                                    const isOdd = blockLen % 2 !== 0;
                                    const myIndex = block.levels.indexOf(level);
                                    // If Odd, center is on the middle Vertebra
                                    const midIndex = Math.floor(blockLen / 2);
                                    const showText = isOdd && (myIndex === midIndex);

                                    const isStart = block.startLevel === level;
                                    const isEnd = block.endLevel === level;
                                    const length = rodLengths[`${side}_${block.startLevel}_${block.endLevel}`];

                                    return { exists: true, isStart, isEnd, length, showText, block };
                                  };

                                  const renderCell = (side) => {
                                    const info = getRodInfo(side);
                                    if (!info) return null;

                                    return (
                                      <div style={{
                                        width: '100%', // Fill the cell completely
                                        backgroundColor: 'var(--bg-overlay)', // Continuous dark grey background
                                        height: 'calc(100% + 2px)', // Minimal overlap to prevent hairline gaps
                                        marginTop: '-1px',
                                        marginBottom: '-1px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        zIndex: 10,
                                        position: 'relative'
                                      }}>
                                        {info.showText && (
                                          <span style={{
                                            fontWeight: 'bold',
                                            color: theme.colors.primary.main,
                                            fontSize: '0.75rem',
                                            whiteSpace: 'nowrap',
                                            zIndex: 20
                                          }}>
                                            {side}: {info.length}
                                          </span>
                                        )}
                                      </div>
                                    );
                                  };

                                  // Symmetric Check
                                  const blocks = getRodBlocks();
                                  const blockL = blocks.L.find(b => b.levels.includes(level));
                                  const blockR = blocks.R.find(b => b.levels.includes(level));
                                  const isSymmetric = blockL && blockR && blockL.startLevel === blockR.startLevel && blockL.endLevel === blockR.endLevel;

                                  if (isSymmetric) {
                                    const blockLen = blockL.levels.length;
                                    const isOdd = blockLen % 2 !== 0;
                                    const midIndex = Math.floor(blockLen / 2);
                                    const myIndex = blockL.levels.indexOf(level);
                                    const showText = isOdd && (myIndex === midIndex);

                                    const isStart = blockL.startLevel === level;
                                    const isEnd = blockL.endLevel === level;

                                    return (
                                      <div style={{
                                        width: '100%',
                                        backgroundColor: 'var(--bg-overlay)',
                                        height: 'calc(100% + 2px)',
                                        marginTop: '-1px',
                                        marginBottom: '-1px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        zIndex: 10,
                                        position: 'relative'
                                      }}>
                                        {showText && (
                                          <span style={{
                                            fontWeight: 'bold',
                                            color: theme.colors.primary.main,
                                            fontSize: '0.8rem',
                                            whiteSpace: 'nowrap',
                                            zIndex: 20
                                          }}>
                                            L: {rodLengths[`L_${blockL.startLevel}_${blockL.endLevel}`]}mm | R: {rodLengths[`R_${blockR.startLevel}_${blockR.endLevel}`]}mm
                                          </span>
                                        )}
                                      </div>
                                    );
                                  }

                                  return (
                                    <div style={{ width: '100%', height: '100%', display: 'flex' }}>
                                      <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                                        {renderCell('L')}
                                      </div>
                                      <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                                        {renderCell('R')}
                                      </div>
                                    </div>
                                  );
                                })()}
                              </div>
                              <div className="pp-cell2 pp-c2-plates">
                                {vertebralInterventions[level] ? (
                                  <div className={
                                    vertebralInterventions[level]?.includes('w/Plate')
                                      ? 'badge badge--accent'
                                      : 'badge badge--neutral'
                                  }>
                                    {vertebralInterventions[level]?.includes('w/Plate') ? 'Yes' : 'No'}
                                  </div>
                                ) : (
                                  <div className="pp-na">N/A</div>
                                )}
                              </div>
                              <div className="pp-cell2 pp-c2-pscrews">
                                {vertebralInterventions[level] && vertebralInterventions[level]?.includes('w/Plate') ? (
                                  <div className="pp-mmbox">
                                    <input
                                      type="text"
                                      className="pp-mini"
                                      placeholder="---"
                                      value={plateScrewLengths[level] || ''}
                                      onChange={(e) => setPlateScrewLengths(prev => ({
                                        ...prev,
                                        [level]: e.target.value
                                      }))}
                                    />
                                    <span className="pp-unit">mm</span>
                                  </div>
                                ) : (
                                  <div className="pp-na">N/A</div>
                                )}
                              </div>
                              {/* Empty cells for disc columns */}
                              <div className="pp-cell2 pp-c2-disc"></div>
                              <div className="pp-cell2 pp-c2-device"></div>
                              <div className="pp-cell2 pp-c2-dscrews"></div>
                            </div>

                            {/* Intervertebral Level Row (if exists) */}
                            {hasInterventebralAfter && (() => {
                              const interLevel = allLevelsToDisplay.intervertebralLevels.find(il => il.startsWith(level + '-'));
                              return (
                                <div key={interLevel} className="pp-t2-row pp-t2-row--disc">
                                  {/* Empty cells for vertebral columns */}
                                  <div className="pp-cell2 pp-c2-level"></div>
                                  <div className="pp-cell2 pp-c2-cages"></div>
                                  <div className="pp-cell2 pp-c2-screws"></div>
                                  <div className="pp-cell2 pp-c2-rods">
                                    {(() => {
                                      // Render Rod Segment in Disc Row

                                      const getDiscRodInfo = (side) => {
                                        const blocks = getRodBlocks()[side];
                                        // Find block covering both above and below
                                        const [above, below] = interLevel.split('-');
                                        const block = blocks.find(b => b.levels.includes(above) && b.levels.includes(below));
                                        if (!block) return null;

                                        const blockLen = block.levels.length;
                                        const isEven = blockLen % 2 === 0;

                                        // If Even, center is on the middle Disc
                                        // Index of 'above' level in disc list:
                                        const myIndex = block.levels.indexOf(above);
                                        const targetIndex = (blockLen / 2) - 1; // 2/2 -1 = 0
                                        const showText = isEven && (myIndex === targetIndex);
                                        const length = rodLengths[`${side}_${block.startLevel}_${block.endLevel}`];

                                        return { exists: true, showText, length };
                                      };

                                      const renderDiscCell = (side) => {
                                        const info = getDiscRodInfo(side);
                                        if (!info) return null;
                                        return (
                                          <div style={{
                                            width: '100%',
                                            backgroundColor: 'var(--bg-overlay)',
                                            height: 'calc(100% + 2px)',
                                            marginTop: '-1px',
                                            marginBottom: '-1px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            zIndex: 10
                                          }}>
                                            {info.showText && (
                                              <span style={{
                                                fontWeight: 'bold',
                                                color: theme.colors.primary.main,
                                                fontSize: '0.75rem',
                                                whiteSpace: 'nowrap',
                                                zIndex: 20
                                              }}>
                                                {side}: {info.length}
                                              </span>
                                            )}
                                          </div>
                                        );
                                      };

                                      // Symmetric Check
                                      const blocks = getRodBlocks();
                                      const [above, below] = interLevel.split('-');
                                      const blockL = blocks.L.find(b => b.levels.includes(above) && b.levels.includes(below));
                                      const blockR = blocks.R.find(b => b.levels.includes(above) && b.levels.includes(below));
                                      const isSymmetric = blockL && blockR && blockL.startLevel === blockR.startLevel && blockL.endLevel === blockR.endLevel;

                                      if (isSymmetric) {
                                        const blockLen = blockL.levels.length;
                                        const isEven = blockLen % 2 === 0;
                                        const myIndex = blockL.levels.indexOf(above);
                                        const targetIndex = (blockLen / 2) - 1;
                                        const showText = isEven && (myIndex === targetIndex);

                                        return (
                                          <div style={{
                                            width: '100%',
                                            backgroundColor: 'var(--bg-overlay)',
                                            height: 'calc(100% + 2px)',
                                            marginTop: '-1px',
                                            marginBottom: '-1px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            zIndex: 10
                                          }}>
                                            {showText && (
                                              <span style={{
                                                fontWeight: 'bold',
                                                color: theme.colors.primary.main,
                                                fontSize: '0.8rem',
                                                whiteSpace: 'nowrap',
                                                zIndex: 20
                                              }}>
                                                L: {rodLengths[`L_${blockL.startLevel}_${blockL.endLevel}`]}mm | R: {rodLengths[`R_${blockR.startLevel}_${blockR.endLevel}`]}mm
                                              </span>
                                            )}
                                          </div>
                                        );
                                      }

                                      return (
                                        <div style={{ width: '100%', height: '100%', display: 'flex' }}>
                                          <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                                            {renderDiscCell('L')}
                                          </div>
                                          <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                                            {renderDiscCell('R')}
                                          </div>
                                        </div>
                                      );

                                    })()}
                                  </div>
                                  <div className="pp-cell2 pp-c2-plates"></div>
                                  <div className="pp-cell2 pp-c2-pscrews"></div>

                                  {/* Disc columns */}
                                  <div className="pp-cell2 pp-c2-disc">{interLevel}</div>
                                  <div className="pp-cell2 pp-c2-device">
                                    <SearchableDropdown
                                      apiEndpoint={`${API_URL}/instrumentation/interbody-devices`}
                                      labelKey="Label"
                                      valueKey="Label"
                                      returnFullObject={true}
                                      placeholder={
                                        interbodyDeviceSelections[interLevel]?.Label
                                          || (intervertebralInterventions[interLevel] ? "Select Device" : "Select Procedure First")
                                      }
                                      disabled={!intervertebralInterventions[interLevel]}
                                      queryParams={(() => {
                                        const intervention = intervertebralInterventions[interLevel] || "";
                                        const section = interLevel.startsWith('C') ? 'Cervical' : 'Lumbar';
                                        let params = { section };

                                        if (intervention.includes("PLIF")) params.name_contains = "PL";
                                        else if (intervention.includes("TLIF")) params.name_contains = "TL";
                                        else if (intervention.includes("ALIF")) params.name_contains = "AL";
                                        else if (intervention.includes("XLIF") || intervention.includes("DLIF") || intervention.includes("LLIF")) {
                                          params.name_excludes = "PL,TL,AL";
                                        }
                                        return params;
                                      })()}
                                      onChange={(value) => {
                                        console.log(`Selected interbody for ${interLevel}:`, value);
                                        // Store the full device selection
                                        setInterbodyDeviceSelections(prev => ({
                                          ...prev,
                                          [interLevel]: { ...value, timestamp: Date.now() }
                                        }));

                                        // --- Parse Device Specs for Auto-Correction ---
                                        if (value) {
                                          const label = value.Label || value.Description || "";

                                          // Parse Angle (e.g. "12deg", "10°", "12 Degree")
                                          const angleMatch = label.match(/(\d+)\s*(?:deg|°|degree)/i);
                                          if (angleMatch) {
                                            const angle = parseFloat(angleMatch[1]);
                                            if (!isNaN(angle)) {
                                              console.log(`[Auto-Correct] Setting ${interLevel} angle to ${angle}° from device`);
                                              setSpineCorrections(prev => ({
                                                ...prev,
                                                [interLevel]: angle
                                              }));
                                            }
                                          }

                                          // Parse Height for Distraction (e.g. "10mm", "H12")
                                          const heightMatch = label.match(/(?:H|Height)\s*(\d+)/i) || label.match(/(\d+)\s*mm/i);
                                          if (heightMatch) {
                                            const height = parseFloat(heightMatch[1]);
                                            if (!isNaN(height)) {
                                              console.log(`[Auto-Correct] Setting ${interLevel} distraction to ${height}mm from device`);
                                              setSpineDistractions(prev => ({
                                                ...prev,
                                                [interLevel]: height
                                              }));
                                            }
                                          }
                                        }
                                      }}
                                    />
                                  </div>
                                  <div className="pp-cell2 pp-c2-dscrews">
                                    {intervertebralInterventions[interLevel] ? (
                                      <div className={
                                        intervertebralInterventions[interLevel] === 'ALIF'
                                          ? 'badge badge--accent'
                                          : 'badge badge--neutral'
                                      }>
                                        {intervertebralInterventions[interLevel] === 'ALIF' ? 'Yes' : 'No'}
                                      </div>
                                    ) : (
                                      <div className="pp-na">N/A</div>
                                    )}
                                  </div>
                                </div>
                              );
                            })()}
                          </React.Fragment>
                        );
                      })}
                      {allLevelsToDisplay.vertebralLevels.length === 0 && (
                        <div className="empty">
                          <div className="empty__title">No hardware to plan</div>
                          <div className="empty__sub">Select vertebral levels on the Measurements step, then choose interventions to populate this table.</div>
                        </div>
                      )}
                    </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PlanningPage;